import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { fetchFeatures, addFeatures, updateFeatures, deleteFeatures } from './featuresAsyncThunk';
import { IAuthResponse } from '../auth/IAuthState';
import { Features } from '../../../types';
import { toast } from 'react-toastify';

export interface DeleteFeatures extends IAuthResponse {
    id: string;
}

interface FeaturesState {
    data: Features[]; // Array of categories
    status: 'idle' | 'loading' | 'succeeded' | 'failed'; // Fetching status
    loading: boolean; // Create/Update loading
    error: string | null;
}

const initialState: FeaturesState = {
    data: [],
    status: 'idle',
    loading: false,
    error: null,
};

// Slice
const FeaturesTypeSlice = createSlice({
    name: 'features',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Fetch Categories
        builder
            .addCase(fetchFeatures.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchFeatures.fulfilled, (state, action: PayloadAction<Features[]>) => {
                state.status = 'succeeded';
                state.data = action.payload;
            })
            .addCase(fetchFeatures.rejected, (state, action) => {
                state.status = 'failed';
                toast.error(action.payload || 'Failed to fetch establishmentType');
                state.error = action.payload || 'Failed to fetch establishmentType';
            });

        // Add Category
        builder
            .addCase(addFeatures.pending, (state) => {
                state.loading = true;
            })
            .addCase(addFeatures.fulfilled, (state, action: PayloadAction<Features>) => {
                state.loading = false;
                state.data.push(action.payload);
            })
            .addCase(addFeatures.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add establishmentType');
                state.error = action.payload || 'Failed to add establishmentType';
            });

        // Update Category
        builder
            .addCase(updateFeatures.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateFeatures.fulfilled, (state, action: PayloadAction<Features>) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
                state.loading = false;
            })
            .addCase(updateFeatures.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add establishmentType');
                state.error = action.payload || 'Failed to update establishmentType';
            });

        // Delete Category
        builder
            .addCase(deleteFeatures.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteFeatures.fulfilled, (state, action: PayloadAction<DeleteFeatures>) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data = state.data.filter((item) => item.id !== action.payload.id);
                }
                state.loading = false;
            })
            .addCase(deleteFeatures.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add pricingPlan');
                state.error = action.payload || 'Failed to delete pricingPlan';
            })
    },
});

export const selectFeatures = (state: RootState) => state.features;


export default FeaturesTypeSlice.reducer;
